"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";

const LINK_CLASS = "font-semibold text-accent underline underline-offset-4";

const CONTENT = {
  fr: {
    heroTitle: "Vide maison à Bruxelles : la checklist à suivre avant l'intervention",
    heroLead:
      "Rien n'est obligatoire de votre côté, mais quelques vérifications simples — tri, documents, accès — permettent d'aborder votre vide maison plus sereinement. Voici la checklist à suivre avant, pendant et après l'intervention.",
    callNow: "Appeler maintenant",
    freeQuote: "Demander un devis gratuit",
    intro:
      "Un vide maison réussi ne dépend pas d'une préparation parfaite : nous intervenons aussi bien dans des biens déjà triés que dans des logements encombrés depuis des années. Cette checklist sert avant tout à éviter les oublis qui comptent vraiment — un document important, un objet de valeur, un accès mal anticipé — et à savoir à quoi s'attendre le jour de l'intervention et une fois le bien évacué. Elle est organisée en trois temps : ce qu'il est utile de vérifier avant notre passage, ce qui se passe concrètement le jour J, et les quelques points à régler une fois le bien vidé. Aucun de ces points n'est une condition pour obtenir un devis : vous pouvez nous contacter même sans avoir encore commencé à préparer quoi que ce soit.",
    situationsTitle: "Dans quelles situations cette checklist est utile",
    situationsA:
      "Que vous videz une maison familiale avant une vente, un appartement dans le cadre d'un déménagement, ou un bien hérité à la suite d'une succession, les mêmes questions reviennent : que garder, quels documents ne pas jeter par erreur, comment organiser l'accès. Dans le cadre d'une vente ou d'une mise en location, le bien doit souvent être libéré à une date précise, ce qui rend la logistique d'accès particulièrement importante. Dans un contexte de succession, la question du tri se double d'une dimension plus sensible : identifier les objets à valeur sentimentale prend du temps, et c'est normal. Pour ce cas précis, notre article ",
    situationsLink: "vide maison après une succession à Bruxelles",
    situationsB: " détaille les démarches propres à cette situation, en coordination avec le notaire et les héritiers si nécessaire.",
    beforeTitle: "Avant l'intervention : la checklist complète",
    beforeIntro:
      "Quatre points à vérifier avant notre passage. Aucun n'est une condition stricte, mais chacun facilite l'organisation du jour J.",
    triHeading: "Le tri : ce que vous gardez à coup sûr",
    tri: [
      "Mettre de côté ce que vous voulez garder à coup sûr : photos, papiers personnels, souvenirs de famille",
      "Repérer les pièces ou espaces déjà triés de ceux qui ne le sont pas encore",
      "Ne pas se sentir obligé de tout trier avant l'intervention : ce n'est pas une condition",
    ],
    triAfter:
      "L'objectif n'est pas de trier l'ensemble du bien, mais de mettre physiquement de côté ce à quoi vous tenez, pour éviter qu'un objet précieux ne parte par erreur avec le reste. Un carton ou une pièce clairement identifiée suffit amplement.",
    documentsHeading: "Les documents à mettre de côté",
    documents: [
      "Actes notariés, contrats et papiers liés à une succession ou une vente",
      "Factures, garanties et carnets d'entretien d'appareils que vous comptez conserver",
      "Correspondance administrative et relevés bancaires encore utiles",
      "Anciennes photos, albums et documents à valeur sentimentale",
    ],
    documentsAfter:
      "Ce sont souvent des papiers mélangés à d'autres affaires — dans un tiroir, une boîte à chaussures, un classeur — qui risquent le plus de partir sans y prêter attention. Un rapide passage en revue des tiroirs et meubles de rangement avant notre intervention permet d'éviter ce type d'oubli, en particulier lorsque le bien fait l'objet d'une succession en cours.",
    valeurHeading: "Les objets potentiellement de valeur à repérer",
    valeur: [
      "Meubles anciens, en bois massif ou signés",
      "Bijoux, montres et argenterie",
      "Vaisselle, verrerie ou porcelaine de collection",
      "Tableaux, gravures et objets de décoration ancienne",
      "Livres anciens, vinyles ou instruments de musique",
    ],
    valeurAfterA:
      "Il n'est pas nécessaire de connaître la valeur exacte de ces objets avant notre passage : c'est justement le rôle de l'évaluation. En cas de doute, mieux vaut ne rien jeter ni donner dans la précipitation, et nous laisser évaluer l'ensemble sur place ou par photos. Le tri des objets valorisables fait partie intégrante de notre intervention, et leur rachat vient en déduction du devis final plutôt que de s'ajouter comme une démarche séparée. Voir notre page ",
    valeurLink1: "rachat d'objets de valeur",
    valeurAfterB: " et notre article ",
    valeurLink2: "comment reconnaître un objet qui vaut la peine d'être racheté",
    valeurAfterC: ".",
    accesHeading: "L'accès et la logistique",
    accesBefore:
      "À Bruxelles, les contraintes d'accès varient beaucoup d'un bien à l'autre : escalier étroit d'une maison de maître, kot étudiant au dernier étage sans ascenseur, immeuble avec parking souterrain limité au centre-ville. Ce sont souvent ces détails, plus que le volume lui-même, qui déterminent l'organisation pratique de l'intervention.",
    acces: [
      "Vérifier l'étage, la présence d'un ascenseur et sa taille",
      "Identifier les possibilités de stationnement pour le camion le jour J",
      "Prévenir le syndic si le bien est en copropriété (accès communs, horaires)",
      "Rassembler les clés, badges et codes d'accès nécessaires",
      "Désigner une personne de contact joignable pendant l'intervention",
    ],
    accesAfterA:
      "Chaque commune bruxelloise a ses propres contraintes de stationnement ou d'accès — retrouvez les informations spécifiques à la vôtre sur notre page ",
    accesLink: "vide maison à Bruxelles, commune par commune",
    accesAfterB: ".",
    dayTitle: "Le jour de l'intervention : à quoi s'attendre",
    dayIntro: "Le déroulement reste simple et suit toujours le même ordre, quel que soit le type de bien.",
    steps: [
      ["Arrivée et tour du bien", "L'équipe fait un rapide tour des lieux avec vous, confirme les objets à conserver et repère les espaces qui demandent une attention particulière (cave, grenier, garage)."],
      ["Repérage des objets valorisables", "Meubles, bijoux, argenterie ou objets de collection sont identifiés avant d'être évacués, pour ne rien perdre par erreur."],
      ["Évacuation complète", "Meubles, encombrants et déchets divers sont sortis et chargés, en respectant l'accès et les contraintes du bien."],
      ["Bien laissé propre", "Le logement est nettoyé sommairement une fois vidé, prêt à être remis en état pour une vente, une location ou une remise de clés."],
    ],
    dayAfterA:
      "La durée totale dépend du volume réel et de l'accès, pas uniquement de la surface du bien — un point détaillé dans notre article sur la ",
    dayLink1: "durée d'un débarras de cave ou grenier",
    dayAfterB: ". Que le bien concerné soit une ",
    dayLink2: "maison",
    dayAfterC: ", un ",
    dayLink3: "appartement",
    dayAfterD: " ou seulement une ",
    dayLink4: "cave, un grenier ou un garage",
    dayAfterE: ", le déroulement reste le même : rien n'est laissé derrière, sauf ce que vous avez identifié comme à conserver.",
    afterTitle: "Après le vide maison : les dernières étapes",
    afterA:
      "Une fois l'évacuation terminée, le bien est laissé propre et prêt à être remis, que ce soit pour une vente, une location ou la remise des clés à un syndic, un notaire ou un nouveau locataire. Les objets rachetés sont déduits directement du devis final, sans démarche supplémentaire de votre part. Il ne reste plus qu'à récupérer les clés et badges que vous nous avez confiés, et éventuellement à informer le syndic ou l'agence que le bien est vidé. Pour savoir ce qu'il advient concrètement des meubles et objets évacués, notre article ",
    afterLink: "que deviennent vos meubles après un débarras",
    afterB: " répond à cette question fréquente.",
    mistakesTitle: "Les erreurs fréquentes à éviter",
    mistakesIntro:
      "Certaines erreurs reviennent souvent et compliquent inutilement la préparation d'un vide maison. Les anticiper permet d'aborder l'intervention avec moins de stress.",
    mistakes: [
      "Jeter ou donner rapidement un meuble ancien sans l'avoir fait estimer au préalable",
      "Oublier de prévenir le syndic alors que le bien est en copropriété, ce qui peut bloquer l'accès le jour J",
      "Mélanger documents personnels et objets à évacuer dans les mêmes cartons",
      "Vouloir tout trier avant de nous contacter, alors qu'un devis peut être établi sans tri préalable",
      "Sous-estimer le temps nécessaire pour un bien encombré depuis plusieurs années, notamment cave et grenier",
    ],
    aloneTitle: "Pas besoin de tout préparer seul",
    aloneA:
      "Cette checklist facilite l'organisation, mais rien n'est obligatoire de notre côté : nous nous adaptons à votre situation, même si le bien est encombré ou si vous n'avez pas eu le temps de trier au préalable. C'est d'ailleurs une situation courante, notamment lors d'une succession ou d'un déménagement organisé dans l'urgence : le manque de temps pour préparer le bien ne retarde pas la possibilité d'obtenir un devis. Un devis gratuit permet d'ajuster la checklist à votre cas précis — retrouvez les facteurs qui influencent le prix sur notre page ",
    aloneLink: "tarifs",
    aloneB: ".",
    retenirTitle: "À retenir",
    aRetenir: [
      "Rien n'est obligatoire de votre côté : le tri complet n'est pas une condition pour démarrer",
      "Mettez d'abord de côté les documents et souvenirs auxquels vous tenez",
      "Le repérage des objets de valeur fait partie de notre intervention, pas seulement de votre préparation",
      "L'accès (étage, ascenseur, stationnement, syndic) influence directement l'organisation du jour J",
      "Un devis gratuit permet d'ajuster la checklist à votre situation réelle",
    ],
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Faut-il tout trier avant l'intervention ?",
        a: "Non. Cette checklist facilite l'organisation, mais rien n'est obligatoire de votre côté. Nous intervenons aussi bien dans des biens déjà triés que dans des logements encombrés depuis longtemps, où aucun tri préalable n'a été fait. Le seul point vraiment utile est de mettre physiquement à part ce que vous souhaitez conserver à coup sûr — documents, souvenirs, objets précieux — pour éviter toute confusion le jour de l'intervention. Le reste du tri, y compris le repérage des objets valorisables, peut être géré par notre équipe sur place.",
      },
      {
        q: "Quels documents faut-il absolument mettre de côté ?",
        a: "Les actes notariés, contrats de vente, factures importantes et garanties d'appareils que vous comptez garder, ainsi que les photos et papiers à valeur sentimentale. En cas de succession, les documents liés au dossier notarial méritent une attention particulière avant toute évacuation, car ils se mélangent facilement à d'autres papiers dans des tiroirs ou classeurs. Un rapide passage en revue des meubles de rangement avant notre passage suffit généralement à éviter les oublis.",
      },
      {
        q: "Comment savoir si un objet vaut la peine d'être fait estimer ?",
        a: "Les meubles anciens en bois massif ou signés, l'argenterie, les bijoux, la vaisselle de collection ou les tableaux méritent un coup d'œil avant d'être évacués. Il n'est pas nécessaire de connaître leur valeur exacte : en cas de doute, le plus simple est de ne rien jeter ni donner dans la précipitation, et de nous laisser évaluer l'ensemble. Le tri des objets valorisables fait partie intégrante de notre intervention, pas d'une démarche séparée.",
      },
      {
        q: "Faut-il prévenir le syndic en cas de copropriété ?",
        a: "Oui, c'est recommandé dès que l'immeuble a des parties communes (ascenseur, hall, cour) ou des horaires d'accès particuliers. Cela évite les blocages le jour de l'intervention, surtout si un camion doit stationner dans une rue étroite ou si l'ascenseur doit être réservé pour transporter des meubles volumineux. Un simple message au syndic quelques jours avant suffit en général.",
      },
      {
        q: "Combien de temps faut-il prévoir pour se préparer ?",
        a: "Cela dépend du volume et de votre disponibilité, mais quelques heures suffisent en général pour mettre de côté l'essentiel : documents, souvenirs, objets déjà identifiés comme précieux. Aucune préparation exhaustive n'est nécessaire avant de nous contacter pour une estimation — le devis peut être établi sur base de photos ou d'une visite, même si le tri n'est pas terminé.",
      },
      {
        q: "Faut-il être présent le jour de l'intervention ?",
        a: "Ce n'est pas obligatoire si l'accès et les modalités sont convenus à l'avance, mais c'est préférable en début d'intervention pour valider ensemble ce qui doit être conservé. Une personne de contact joignable par téléphone pendant la journée reste utile dans tous les cas, notamment pour lever un doute sur un objet.",
      },
      {
        q: "Que se passe-t-il si je n'ai pas eu le temps de trier ?",
        a: "Ce n'est pas un problème : nous nous adaptons à des biens encombrés où rien n'a été trié au préalable, y compris après plusieurs années d'accumulation. Le repérage des objets de valeur et l'organisation complète de l'évacuation restent de notre ressort, quel que soit l'état de préparation du logement au moment de notre arrivée.",
      },
      {
        q: "Le rachat d'objets de valeur repérés grâce à la checklist change-t-il le devis ?",
        a: "Oui : lorsque des objets valorisables sont identifiés, leur rachat est déduit directement du devis final plutôt que traité comme une démarche séparée. Cela ne modifie pas l'organisation de l'intervention, mais peut réduire sensiblement le coût final selon ce qui est repéré dans le bien.",
      },
    ],
    ctaFinalTitle: "Prêt à organiser votre vide maison ?",
    ctaFinalText: "Décrivez votre situation, nous vous répondons avec un devis gratuit et sans engagement.",
    ctaFinalQuote: "Devis gratuit",
  },
  nl: {
    heroTitle: "Huis leegmaken in Brussel: de checklist vóór de interventie",
    heroLead:
      "Er is niets verplicht van uw kant, maar een paar eenvoudige controles — sorteren, documenten, toegang — helpen u om uw huis leegmaken rustiger aan te pakken. Hier is de checklist te volgen vóór, tijdens en na de interventie.",
    callNow: "Bel nu",
    freeQuote: "Vraag een gratis offerte aan",
    intro:
      "Een geslaagd huis leegmaken hangt niet af van een perfecte voorbereiding: wij komen tussen in reeds gesorteerde panden, maar evengoed in woningen die al jaren vol staan. Deze checklist dient vooral om vergissingen te vermijden die er echt toe doen — een belangrijk document, een waardevol voorwerp, een slecht ingeschatte toegang — en om te weten wat u kunt verwachten op de dag van de interventie en zodra het pand leeg is. Ze is opgebouwd in drie fasen: wat nuttig is om te controleren vóór ons bezoek, wat er concreet gebeurt op de dag zelf, en de laatste punten om te regelen zodra het pand leeg is. Geen van deze punten is een voorwaarde om een offerte te krijgen: u kunt ons contacteren, ook als u nog niets heeft voorbereid.",
    situationsTitle: "In welke situaties is deze checklist nuttig",
    situationsA:
      "Of u nu een gezinswoning leegmaakt vóór een verkoop, een appartement in het kader van een verhuizing, of een geërfd pand na een nalatenschap, dezelfde vragen komen steeds terug: wat te bewaren, welke documenten niet per ongeluk weg te gooien, hoe de toegang te organiseren. Bij een verkoop of verhuur moet het pand vaak op een precieze datum worden vrijgemaakt, wat de logistiek van de toegang bijzonder belangrijk maakt. Bij een nalatenschap krijgt het sorteren een gevoeligere dimensie: voorwerpen met sentimentele waarde identificeren kost tijd, en dat is normaal. Voor dit specifieke geval behandelt ons artikel ",
    situationsLink: "huis leegmaken na een nalatenschap in Brussel",
    situationsB: " de stappen die eigen zijn aan deze situatie, indien nodig in overleg met de notaris en de erfgenamen.",
    beforeTitle: "Vóór de interventie: de volledige checklist",
    beforeIntro:
      "Vier punten om te controleren vóór ons bezoek. Geen enkel is een strikte voorwaarde, maar elk vergemakkelijkt de organisatie van de dag zelf.",
    triHeading: "Het sorteren: wat u zeker bewaart",
    tri: [
      "Zet apart wat u zeker wilt bewaren: foto's, persoonlijke papieren, familieherinneringen",
      "Identificeer welke kamers of ruimtes al gesorteerd zijn en welke nog niet",
      "Voel u niet verplicht om alles te sorteren vóór de interventie: dat is geen voorwaarde",
    ],
    triAfter:
      "Het doel is niet om het volledige pand te sorteren, maar om fysiek apart te zetten wat u dierbaar is, zodat een waardevol voorwerp niet per ongeluk met de rest meegaat. Een doos of een duidelijk aangeduide kamer volstaat ruimschoots.",
    documentsHeading: "De documenten om apart te zetten",
    documents: [
      "Notariële akten, contracten en papieren met betrekking tot een nalatenschap of verkoop",
      "Facturen, garanties en onderhoudsboekjes van toestellen die u wilt behouden",
      "Administratieve briefwisseling en nog nuttige bankafschriften",
      "Oude foto's, albums en documenten met sentimentele waarde",
    ],
    documentsAfter:
      "Dit zijn vaak papieren die vermengd zijn met andere spullen — in een lade, een schoenendoos, een map — en die het meeste risico lopen om zonder aandacht mee te vertrekken. Een snelle controle van laden en opbergmeubels vóór onze interventie volstaat meestal om dit soort vergetelheden te vermijden, in het bijzonder wanneer het pand deel uitmaakt van een lopende nalatenschap.",
    valeurHeading: "De mogelijk waardevolle voorwerpen om te identificeren",
    valeur: [
      "Antieke meubels, massief hout of gesigneerd",
      "Juwelen, horloges en tafelzilver",
      "Verzamelservies, glaswerk of porselein",
      "Schilderijen, gravures en oude decoratievoorwerpen",
      "Oude boeken, vinylplaten of muziekinstrumenten",
    ],
    valeurAfterA:
      "Het is niet nodig om de exacte waarde van deze voorwerpen te kennen vóór ons bezoek: dat is precies de rol van de schatting. Bij twijfel is het het eenvoudigst om niets overhaast weg te gooien of weg te geven, en ons het geheel te laten schatten ter plaatse of via foto's. Het sorteren van waardevolle voorwerpen maakt integraal deel uit van onze interventie, en de inkoop ervan wordt in mindering gebracht van de eindofferte in plaats van als een aparte stap te worden toegevoegd. Zie onze pagina ",
    valeurLink1: "inkoop van waardevolle voorwerpen",
    valeurAfterB: " en ons artikel ",
    valeurLink2: "hoe herkent u een voorwerp dat het waard is om te worden ingekocht",
    valeurAfterC: ".",
    accesHeading: "De toegang en de logistiek",
    accesBefore:
      "In Brussel verschillen de toegangsbeperkingen sterk van pand tot pand: een smalle trap in een herenhuis, een studentenkot op de bovenste verdieping zonder lift, een gebouw met beperkte ondergrondse parking in het centrum. Het zijn vaak deze details, meer nog dan het volume zelf, die de praktische organisatie van de interventie bepalen.",
    acces: [
      "Controleer de verdieping, de aanwezigheid van een lift en de grootte ervan",
      "Ga na welke parkeermogelijkheden er zijn voor de vrachtwagen op de dag zelf",
      "Verwittig de syndicus als het pand een mede-eigendom is (gemeenschappelijke toegang, uren)",
      "Verzamel de sleutels, badges en nodige toegangscodes",
      "Wijs een bereikbare contactpersoon aan tijdens de interventie",
    ],
    accesAfterA:
      "Elke Brusselse gemeente heeft haar eigen beperkingen inzake parkeren of toegang — vind de specifieke informatie voor uw gemeente op onze pagina ",
    accesLink: "huis leegmaken in Brussel, gemeente per gemeente",
    accesAfterB: ".",
    dayTitle: "De dag van de interventie: wat te verwachten",
    dayIntro: "Het verloop blijft eenvoudig en volgt steeds dezelfde volgorde, ongeacht het type pand.",
    steps: [
      ["Aankomst en rondgang door het pand", "Het team maakt samen met u een korte rondgang, bevestigt welke voorwerpen behouden blijven en identificeert de ruimtes die bijzondere aandacht vragen (kelder, zolder, garage)."],
      ["Identificatie van waardevolle voorwerpen", "Meubels, juwelen, tafelzilver of verzamelvoorwerpen worden geïdentificeerd vóór ze worden weggehaald, zodat er niets per ongeluk verloren gaat."],
      ["Volledige ontruiming", "Meubels, grofvuil en diverse afval worden buiten gebracht en geladen, met respect voor de toegang en de beperkingen van het pand."],
      ["Pand netjes achtergelaten", "De woning wordt summier gereinigd zodra ze leeg is, klaar om te worden opgeleverd voor een verkoop, verhuur of sleuteloverdracht."],
    ],
    dayAfterA:
      "De totale duur hangt af van het werkelijke volume en de toegang, niet enkel van de oppervlakte van het pand — een punt dat wordt toegelicht in ons artikel over de ",
    dayLink1: "duur van het ontruimen van een kelder of zolder",
    dayAfterB: ". Of het nu gaat om een ",
    dayLink2: "huis",
    dayAfterC: ", een ",
    dayLink3: "appartement",
    dayAfterD: " of enkel een ",
    dayLink4: "kelder, zolder of garage",
    dayAfterE: ", het verloop blijft hetzelfde: niets wordt achtergelaten, behalve wat u heeft aangeduid om te behouden.",
    afterTitle: "Na het leegmaken: de laatste stappen",
    afterA:
      "Zodra de ontruiming is voltooid, wordt het pand netjes achtergelaten en klaar om te worden opgeleverd, of het nu gaat om een verkoop, een verhuur of de sleuteloverdracht aan een syndicus, een notaris of een nieuwe huurder. De ingekochte voorwerpen worden rechtstreeks in mindering gebracht van de eindofferte, zonder bijkomende stap van uw kant. Er rest u enkel nog de sleutels en badges terug te krijgen die u ons had toevertrouwd, en eventueel de syndicus of het agentschap te informeren dat het pand leeg is. Om te weten wat er concreet gebeurt met de meubels en voorwerpen die worden weggehaald, beantwoordt ons artikel ",
    afterLink: "wat gebeurt er met uw meubels na een ontruiming",
    afterB: " deze veelgestelde vraag.",
    mistakesTitle: "Veelgemaakte fouten om te vermijden",
    mistakesIntro:
      "Bepaalde fouten komen vaak terug en bemoeilijken nodeloos de voorbereiding van een huis leegmaken. Ze anticiperen maakt het mogelijk om de interventie met minder stress aan te pakken.",
    mistakes: [
      "Een antiek meubelstuk snel weggooien of weggeven zonder het vooraf te laten schatten",
      "Vergeten de syndicus te verwittigen terwijl het pand een mede-eigendom is, wat de toegang op de dag zelf kan blokkeren",
      "Persoonlijke documenten en te evacueren voorwerpen mengen in dezelfde dozen",
      "Alles willen sorteren vooraleer u ons contacteert, terwijl een offerte kan worden opgesteld zonder voorafgaand sorteren",
      "De nodige tijd onderschatten voor een pand dat al jaren vol staat, met name kelder en zolder",
    ],
    aloneTitle: "U hoeft niet alles alleen voor te bereiden",
    aloneA:
      "Deze checklist vergemakkelijkt de organisatie, maar niets is verplicht van onze kant: wij passen ons aan uw situatie aan, ook als het pand vol staat of als u geen tijd heeft gehad om vooraf te sorteren. Dit is trouwens een veelvoorkomende situatie, met name bij een nalatenschap of een verhuizing die dringend georganiseerd moet worden: het gebrek aan tijd om het pand voor te bereiden vertraagt de mogelijkheid om een offerte te krijgen niet. Een gratis offerte laat toe de checklist aan te passen aan uw specifieke situatie — vind de factoren die de prijs beïnvloeden op onze pagina ",
    aloneLink: "tarieven",
    aloneB: ".",
    retenirTitle: "Om te onthouden",
    aRetenir: [
      "Niets is verplicht van uw kant: volledig sorteren is geen voorwaarde om te starten",
      "Zet eerst de documenten en herinneringen apart die u dierbaar zijn",
      "Het identificeren van waardevolle voorwerpen maakt deel uit van onze interventie, niet enkel van uw voorbereiding",
      "De toegang (verdieping, lift, parkeren, syndicus) beïnvloedt rechtstreeks de organisatie van de dag zelf",
      "Een gratis offerte laat toe de checklist aan te passen aan uw werkelijke situatie",
    ],
    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Moet alles gesorteerd zijn vóór de interventie?",
        a: "Nee. Deze checklist vergemakkelijkt de organisatie, maar niets is verplicht van uw kant. Wij komen evengoed tussen in reeds gesorteerde panden als in woningen die al lang vol staan, waar geen enkele voorafgaande sortering is gebeurd. Het enige echt nuttige punt is fysiek apart te zetten wat u zeker wilt bewaren — documenten, herinneringen, waardevolle voorwerpen — om elke verwarring op de dag van de interventie te vermijden. De rest van het sorteren, inclusief het identificeren van waardevolle voorwerpen, kan door ons team ter plaatse worden beheerd.",
      },
      {
        q: "Welke documenten moet u absoluut apart zetten?",
        a: "Notariële akten, verkoopcontracten, belangrijke facturen en garanties van toestellen die u wilt behouden, evenals foto's en papieren met sentimentele waarde. Bij een nalatenschap verdienen de documenten die verband houden met het notarieel dossier bijzondere aandacht vóór elke ontruiming, want ze vermengen zich gemakkelijk met andere papieren in laden of mappen. Een snelle controle van de opbergmeubels vóór ons bezoek volstaat doorgaans om vergetelheden te vermijden.",
      },
      {
        q: "Hoe weet u of een voorwerp de moeite waard is om te laten schatten?",
        a: "Antieke meubels in massief hout of gesigneerd, tafelzilver, juwelen, verzamelservies of schilderijen verdienen een blik vóór ze worden weggehaald. Het is niet nodig om hun exacte waarde te kennen: bij twijfel is het eenvoudigst om niets overhaast weg te gooien of weg te geven, en ons het geheel te laten schatten. Het sorteren van waardevolle voorwerpen maakt integraal deel uit van onze interventie, niet van een aparte stap.",
      },
      {
        q: "Moet u de syndicus verwittigen bij een mede-eigendom?",
        a: "Ja, dat wordt aangeraden zodra het gebouw gemeenschappelijke delen heeft (lift, hal, binnenplaats) of specifieke toegangsuren. Dit vermijdt blokkeringen op de dag van de interventie, vooral als een vrachtwagen moet parkeren in een smalle straat of als de lift moet worden gereserveerd voor het vervoer van omvangrijke meubels. Een eenvoudig bericht aan de syndicus enkele dagen op voorhand volstaat meestal.",
      },
      {
        q: "Hoeveel tijd moet u voorzien om zich voor te bereiden?",
        a: "Dat hangt af van het volume en uw beschikbaarheid, maar een paar uur volstaat doorgaans om het essentiële apart te zetten: documenten, herinneringen, reeds als waardevol geïdentificeerde voorwerpen. Geen uitgebreide voorbereiding is nodig vóór u ons contacteert voor een schatting — de offerte kan worden opgesteld op basis van foto's of een bezoek, ook als het sorteren nog niet is afgerond.",
      },
      {
        q: "Moet u aanwezig zijn op de dag van de interventie?",
        a: "Dat is niet verplicht als de toegang en de modaliteiten vooraf zijn afgesproken, maar het is aan te raden bij het begin van de interventie om samen te bevestigen wat behouden moet blijven. Een telefonisch bereikbare contactpersoon gedurende de dag blijft in elk geval nuttig, met name om twijfel over een voorwerp weg te nemen.",
      },
      {
        q: "Wat gebeurt er als u geen tijd heeft gehad om te sorteren?",
        a: "Dat is geen probleem: wij passen ons aan aan volle panden waar niets vooraf werd gesorteerd, ook na meerdere jaren van opeenstapeling. Het identificeren van waardevolle voorwerpen en de volledige organisatie van de ontruiming blijven onze verantwoordelijkheid, ongeacht de staat van voorbereiding van de woning bij onze aankomst.",
      },
      {
        q: "Verandert de inkoop van waardevolle voorwerpen, geïdentificeerd dankzij de checklist, de offerte?",
        a: "Ja: wanneer waardevolle voorwerpen worden geïdentificeerd, wordt hun inkoop rechtstreeks in mindering gebracht van de eindofferte in plaats van als een aparte stap te worden behandeld. Dit verandert niets aan de organisatie van de interventie, maar kan de eindkost aanzienlijk verlagen, afhankelijk van wat er in het pand wordt aangetroffen.",
      },
    ],
    ctaFinalTitle: "Klaar om uw huis leegmaken te organiseren?",
    ctaFinalText: "Beschrijf uw situatie, wij antwoorden met een gratis en vrijblijvende offerte.",
    ctaFinalQuote: "Gratis offerte",
  },
  en: {
    heroTitle: "House clearance in Brussels: the checklist to follow before the intervention",
    heroLead:
      "Nothing is mandatory on your part, but a few simple checks — sorting, documents, access — help you approach your house clearance more calmly. Here is the checklist to follow before, during and after the intervention.",
    callNow: "Call now",
    freeQuote: "Request a free quote",
    intro:
      "A successful house clearance does not depend on perfect preparation: we work just as well in properties that have already been sorted as in homes that have been cluttered for years. This checklist is above all meant to avoid the oversights that really matter — an important document, a valuable item, poorly anticipated access — and to know what to expect on the day of the intervention and once the property has been cleared. It is organised in three stages: what is useful to check before our visit, what actually happens on the day itself, and the few points to settle once the property is empty. None of these points is a condition for obtaining a quote: you can contact us even without having started to prepare anything yet.",
    situationsTitle: "In which situations is this checklist useful",
    situationsA:
      "Whether you are clearing a family home before a sale, an apartment as part of a move, or a property inherited following an estate settlement, the same questions come up: what to keep, which documents not to throw away by mistake, how to organise access. For a sale or a rental, the property often has to be vacated by a precise date, which makes access logistics particularly important. In an inheritance context, sorting takes on a more sensitive dimension: identifying items with sentimental value takes time, and that is normal. For this specific case, our article ",
    situationsLink: "house clearance after an inheritance in Brussels",
    situationsB: " details the steps specific to this situation, in coordination with the notary and the heirs if necessary.",
    beforeTitle: "Before the intervention: the full checklist",
    beforeIntro:
      "Four points to check before our visit. None is a strict condition, but each makes organising the day itself easier.",
    triHeading: "Sorting: what you are certain to keep",
    tri: [
      "Set aside what you definitely want to keep: photos, personal papers, family keepsakes",
      "Identify which rooms or spaces have already been sorted and which have not",
      "Do not feel obliged to sort everything before the intervention: this is not a requirement",
    ],
    triAfter:
      "The goal is not to sort the entire property, but to physically set aside what matters to you, to avoid a precious item leaving by mistake with the rest. A clearly identified box or room is more than enough.",
    documentsHeading: "The documents to set aside",
    documents: [
      "Notarial deeds, contracts and papers related to an inheritance or a sale",
      "Invoices, warranties and maintenance booklets for appliances you intend to keep",
      "Administrative correspondence and still-useful bank statements",
      "Old photos, albums and documents with sentimental value",
    ],
    documentsAfter:
      "These are often papers mixed in with other belongings — in a drawer, a shoebox, a folder — that are most at risk of being taken away without notice. A quick check of drawers and storage furniture before our intervention is usually enough to avoid this kind of oversight, particularly when the property is part of an ongoing inheritance.",
    valeurHeading: "Potentially valuable items to spot",
    valeur: [
      "Antique furniture, solid wood or signed pieces",
      "Jewellery, watches and silverware",
      "Collectible tableware, glassware or porcelain",
      "Paintings, engravings and antique decorative items",
      "Old books, vinyl records or musical instruments",
    ],
    valeurAfterA:
      "There is no need to know the exact value of these items before our visit: that is precisely the role of the appraisal. If in doubt, the simplest approach is not to throw away or give away anything in haste, and to let us assess everything on site or from photos. Sorting valuable items is an integral part of our intervention, and their buyback is deducted directly from the final quote rather than added as a separate step. See our page ",
    valeurLink1: "buyback of valuable items",
    valeurAfterB: " and our article ",
    valeurLink2: "how to recognise an item worth buying back",
    valeurAfterC: ".",
    accesHeading: "Access and logistics",
    accesBefore:
      "In Brussels, access constraints vary a great deal from one property to another: a narrow staircase in a townhouse, a student room on the top floor with no lift, a building with limited underground parking in the city centre. It is often these details, more than the volume itself, that determine the practical organisation of the intervention.",
    acces: [
      "Check the floor, whether there is a lift and its size",
      "Identify parking options for the truck on the day",
      "Notify the building manager if the property is co-owned (shared access, hours)",
      "Gather the keys, badges and access codes needed",
      "Designate a reachable contact person during the intervention",
    ],
    accesAfterA:
      "Each Brussels commune has its own parking or access constraints — find the information specific to yours on our page ",
    accesLink: "house clearance in Brussels, commune by commune",
    accesAfterB: ".",
    dayTitle: "On the day of the intervention: what to expect",
    dayIntro: "The process stays simple and always follows the same order, whatever the type of property.",
    steps: [
      ["Arrival and walk-through", "The team does a quick walk-through of the property with you, confirms which items are to be kept and identifies spaces that need particular attention (cellar, attic, garage)."],
      ["Identifying valuable items", "Furniture, jewellery, silverware or collectible items are identified before removal, so nothing is lost by mistake."],
      ["Full clearance", "Furniture, bulky items and various waste are taken out and loaded, in line with the property's access and constraints."],
      ["Property left clean", "The property is given a basic clean once cleared, ready to be handed over for a sale, a rental or a key handover."],
    ],
    dayAfterA:
      "The total duration depends on the actual volume and access, not just the surface area of the property — a point covered in detail in our article on the ",
    dayLink1: "duration of clearing a cellar or attic",
    dayAfterB: ". Whether the property concerned is a ",
    dayLink2: "house",
    dayAfterC: ", an ",
    dayLink3: "apartment",
    dayAfterD: " or just a ",
    dayLink4: "cellar, attic or garage",
    dayAfterE: ", the process stays the same: nothing is left behind, except what you have identified as to be kept.",
    afterTitle: "After the house clearance: the final steps",
    afterA:
      "Once the clearance is complete, the property is left clean and ready to be handed over, whether for a sale, a rental or the handover of keys to a building manager, a notary or a new tenant. Bought-back items are deducted directly from the final quote, with no extra step on your part. All that remains is to collect the keys and badges you entrusted to us, and if needed, to inform the building manager or agency that the property is empty. To find out what actually happens to furniture and items removed, our article ",
    afterLink: "what happens to your furniture after a clearance",
    afterB: " answers this frequently asked question.",
    mistakesTitle: "Common mistakes to avoid",
    mistakesIntro:
      "Certain mistakes come up often and needlessly complicate preparing for a house clearance. Anticipating them makes it possible to approach the intervention with less stress.",
    mistakes: [
      "Throwing away or giving away an antique piece of furniture quickly without having it valued beforehand",
      "Forgetting to notify the building manager when the property is co-owned, which can block access on the day",
      "Mixing personal documents and items to be removed in the same boxes",
      "Wanting to sort everything before contacting us, when a quote can be drawn up without prior sorting",
      "Underestimating the time needed for a property cluttered for several years, particularly cellars and attics",
    ],
    aloneTitle: "No need to prepare everything alone",
    aloneA:
      "This checklist makes organising easier, but nothing is mandatory on our part: we adapt to your situation, even if the property is cluttered or if you have not had time to sort beforehand. This is in fact a common situation, particularly during an inheritance or a move organised urgently: a lack of time to prepare the property does not delay the possibility of obtaining a quote. A free quote makes it possible to adjust the checklist to your specific case — find the factors that influence the price on our page ",
    aloneLink: "prices",
    aloneB: ".",
    retenirTitle: "Key takeaways",
    aRetenir: [
      "Nothing is mandatory on your part: full sorting is not a condition to get started",
      "First set aside the documents and keepsakes that matter to you",
      "Spotting valuable items is part of our intervention, not just your preparation",
      "Access (floor, lift, parking, building manager) directly influences the organisation of the day",
      "A free quote makes it possible to adjust the checklist to your actual situation",
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "Do you have to sort everything before the intervention?",
        a: "No. This checklist makes organising easier, but nothing is mandatory on your part. We work just as well in properties that have already been sorted as in homes that have been cluttered for a long time, where no prior sorting has been done. The only truly useful point is to physically set aside what you definitely want to keep — documents, keepsakes, precious items — to avoid any confusion on the day of the intervention. The rest of the sorting, including spotting valuable items, can be handled by our team on site.",
      },
      {
        q: "Which documents absolutely need to be set aside?",
        a: "Notarial deeds, sale contracts, important invoices and warranties for appliances you plan to keep, as well as photos and papers with sentimental value. In the case of an inheritance, documents related to the notarial file deserve particular attention before any clearance, as they easily get mixed up with other papers in drawers or folders. A quick review of storage furniture before our visit is usually enough to avoid oversights.",
      },
      {
        q: "How do you know if an item is worth having valued?",
        a: "Antique furniture in solid wood or signed pieces, silverware, jewellery, collectible tableware or paintings deserve a look before being removed. There is no need to know their exact value: if in doubt, the simplest approach is not to throw away or give away anything in haste, and to let us assess everything. Sorting valuable items is an integral part of our intervention, not a separate step.",
      },
      {
        q: "Do you need to notify the building manager for a co-owned property?",
        a: "Yes, this is recommended as soon as the building has shared areas (lift, hallway, courtyard) or specific access hours. This avoids obstacles on the day of the intervention, especially if a truck needs to park on a narrow street or if the lift needs to be reserved to move bulky furniture. A simple message to the building manager a few days in advance is usually enough.",
      },
      {
        q: "How much time should you set aside to prepare?",
        a: "This depends on the volume and your availability, but a few hours are usually enough to set aside the essentials: documents, keepsakes, items already identified as precious. No exhaustive preparation is needed before contacting us for an estimate — the quote can be drawn up based on photos or a visit, even if sorting is not finished.",
      },
      {
        q: "Do you need to be present on the day of the intervention?",
        a: "This is not mandatory if access and arrangements have been agreed in advance, but it is preferable at the start of the intervention to confirm together what should be kept. A contact person reachable by phone during the day remains useful in any case, particularly to settle doubts about an item.",
      },
      {
        q: "What happens if you have not had time to sort?",
        a: "That is not a problem: we adapt to cluttered properties where nothing has been sorted beforehand, even after several years of accumulation. Spotting valuable items and fully organising the clearance remain our responsibility, whatever the state of preparation of the home when we arrive.",
      },
      {
        q: "Does the buyback of valuable items spotted thanks to the checklist change the quote?",
        a: "Yes: when valuable items are identified, their buyback is deducted directly from the final quote rather than treated as a separate step. This does not change the organisation of the intervention, but it can significantly reduce the final cost depending on what is found in the property.",
      },
    ],
    ctaFinalTitle: "Ready to organise your house clearance?",
    ctaFinalText: "Describe your situation, and we will get back to you with a free, no-obligation quote.",
    ctaFinalQuote: "Free quote",
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <article>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(63,108,81,0.08), transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.heroTitle}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-ink/70 leading-relaxed">
            {c.heroLead}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800"
            >
              <PhoneIcon className="h-5 w-5" />
              {c.callNow}
            </a>
            <a
              href="#contactus"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent"
            >
              {c.freeQuote}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <p className="leading-relaxed text-neutral-700">{c.intro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.situationsTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.situationsA}
              <Link href="/blog/vide-maison-succession-bruxelles" className={LINK_CLASS}>
                {c.situationsLink}
              </Link>
              {c.situationsB}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.beforeTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.beforeIntro}
            </p>

            <div className="mt-6 space-y-6">
              <div>
                <div className="flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-accent" />
                  <p className="font-serif text-lg font-bold text-ink">{c.triHeading}</p>
                </div>
                <ul className="mt-3 space-y-2">
                  {c.tri.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                      <span className="text-sm font-semibold text-ink">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 leading-relaxed text-neutral-700">{c.triAfter}</p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-accent" />
                  <p className="font-serif text-lg font-bold text-ink">{c.documentsHeading}</p>
                </div>
                <ul className="mt-3 space-y-2">
                  {c.documents.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                      <span className="text-sm font-semibold text-ink">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 leading-relaxed text-neutral-700">{c.documentsAfter}</p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-accent" />
                  <p className="font-serif text-lg font-bold text-ink">{c.valeurHeading}</p>
                </div>
                <ul className="mt-3 space-y-2">
                  {c.valeur.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                      <span className="text-sm font-semibold text-ink">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 leading-relaxed text-neutral-700">
                  {c.valeurAfterA}
                  <Link href="/rachat-objets" className={LINK_CLASS}>
                    {c.valeurLink1}
                  </Link>
                  {c.valeurAfterB}
                  <Link href="/blog/reconnaitre-objet-valeur-rachat" className={LINK_CLASS}>
                    {c.valeurLink2}
                  </Link>
                  {c.valeurAfterC}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <KeyIcon className="h-5 w-5 text-accent" />
                  <p className="font-serif text-lg font-bold text-ink">{c.accesHeading}</p>
                </div>
                <p className="mt-3 leading-relaxed text-neutral-700">{c.accesBefore}</p>
                <ul className="mt-3 space-y-2">
                  {c.acces.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                      <span className="text-sm font-semibold text-ink">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 leading-relaxed text-neutral-700">
                  {c.accesAfterA}
                  <Link href="/vide-maison" className={LINK_CLASS}>
                    {c.accesLink}
                  </Link>
                  {c.accesAfterB}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.dayTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.dayIntro}
            </p>
            <ol className="mt-4 space-y-4">
              {c.steps.map(([title, desc], i) => (
                <li key={title} className="flex gap-4 rounded-2xl border border-sand-line bg-sand p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-bold text-ink">{title}</p>
                    <p className="mt-1 text-sm text-neutral-600">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.dayAfterA}
              <Link href="/blog/duree-debarras-cave-grenier" className={LINK_CLASS}>
                {c.dayLink1}
              </Link>
              {c.dayAfterB}
              <Link href="/debarras-maison" className={LINK_CLASS}>
                {c.dayLink2}
              </Link>
              {c.dayAfterC}
              <Link href="/debarras-appartement" className={LINK_CLASS}>
                {c.dayLink3}
              </Link>
              {c.dayAfterD}
              <Link href="/debarras-cave-grenier-garage" className={LINK_CLASS}>
                {c.dayLink4}
              </Link>
              {c.dayAfterE}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.afterTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.afterA}
              <Link href="/blog/que-deviennent-meubles-apres-debarras" className={LINK_CLASS}>
                {c.afterLink}
              </Link>
              {c.afterB}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.mistakesTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.mistakesIntro}
            </p>
            <ul className="mt-4 space-y-2">
              {c.mistakes.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.aloneTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.aloneA}
              <Link href="/tarifs" className={LINK_CLASS}>
                {c.aloneLink}
              </Link>
              {c.aloneB}
            </p>
          </div>

          <div className="rounded-3xl border border-sand-line bg-sand p-6">
            <h2 className="font-serif text-xl font-extrabold text-brand-900">{c.retenirTitle}</h2>
            <ul className="mt-4 space-y-2">
              {c.aRetenir.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.ctaFinalTitle}</h2>
            <p className="mt-2 text-white/75">
              {c.ctaFinalText}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800"
              >
                <PhoneIcon className="h-5 w-5" />
                {c.callNow}
              </a>
              <a
                href="#contactus"
                className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20"
              >
                <CheckCircleIcon className="h-5 w-5" />
                {c.ctaFinalQuote}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
